import {
  userInfoStore,
  roundInfoStore,
  gameConfigStore,
  applicationStore,
  popupStore,
} from '@pascal/pascal-core/shared/core/store';
import {
  BetStatus,
  freeBetStore,
  DefaultQueryParams,
  GameModes,
  notificationStore,
  parseQuery,
  tableBetHistoryStore,
  loadingStore,
} from '@pascal/pascal-core';
import { gameStore } from '../store/gameStore';
import { doBetStore } from '../store/doBetStore';
import { cardsStore } from '../store/cardsStore';
import { soundService, soundTypes } from './SoundService';
import { liveBetsDataStore } from '../store/liveBetsDataStore';
import { winningStore } from '../store/winningStore';
import { gameLiveConnection } from './GameConnection';
import { betResponseCodeByName } from '../enums/betResponseCode';
import { UNKNOWN_CARD_TYPE } from '../enums/cardTypes';

class TurboGameService {
  onGetInitialStateResult({
    rate,
    limits,
    balance,
    liveBets,
    precision,
    roundInfo,
    currentBet,
    currencyCode,
    isTestPlatform,
    underMaintenance,
    userFreebets,
  }) {
    applicationStore.setTranslator((arg) => arg);
    applicationStore.setUseDemoByBackendLogic(false);
    this.handleResetDataInitial();
    const {
      [DefaultQueryParams.CurrencySymbol]: currencySymbol,
      [DefaultQueryParams.UserId]: userId,
      [DefaultQueryParams.Mode]: mode,
    } = parseQuery(window.location.search);
    userInfoStore.setUserInfo({
      rate,
      userId,
      balance,
      precision,
      currencyCode,
      currencySymbol,
    });
    popupStore.setReloadFrameForUnderMaintenanceOnRoundStart(true);
    if (underMaintenance) {
      popupStore.startUnderMaintenanceFunctional(underMaintenance);
    }
    liveBetsDataStore.setLiveBets(liveBets);
    gameConfigStore.setLimits(limits);
    applicationStore.setIsDemo(mode === GameModes.Fun || isTestPlatform);
    applicationStore.setIsTestPlatform(isTestPlatform);
    doBetStore.setQuickBets(limits?.quickBets);
    if (!freeBetStore.activeFreeBet.id) {
      doBetStore.setSelectedAmount(
        limits?.quickBets[1] > balance ? limits?.quickBets[0] : limits?.quickBets[1]
      );
    }
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    if (currentBet) {
      if (currentBet.freeBetId) {
        freeBetStore.setActivateFreeBet(
          userFreebets.find((item) => item.id === currentBet.freeBetId)
        );
      }
      gameStore.setIsBetsClosed(true);
      gameStore.setBetId(currentBet.id);
      gameStore.setRoundStatus(currentBet.status);
      roundInfoStore.setRoundId(roundInfo.roundId);
      if (currentBet.gambleCards && currentBet.gambleCards.length) {
        doBetStore.setGambleType(currentBet.additionalParams.gambleType);
        doBetStore.setIsShowGamble(true);
        winningStore.setWinCombination(currentBet.additionalParams.winCombination);
        if (currentBet.gambleCards.some((item) => item.card === UNKNOWN_CARD_TYPE)) {
          cardsStore.setIsPickHoldActive(true);
          winningStore.setGambleWin(currentBet.winAmount);
          setTimeout(() => {
            cardsStore.setWithOutWinCards(currentBet.gambleCards);
          }, 300);
        } else {
          winningStore.setGambleWin(currentBet.winAmount);
          setTimeout(() => {
            cardsStore.setWinInCards(currentBet.gambleCards, true);
          }, 300);
        }
      } else {
        setTimeout(() => {
          cardsStore.setWithOutWinCards(roundInfo.roundData.initialCards);
        }, 300);
        this.handleRoundWinning({
          winAmount: currentBet.winAmount,
          cards: roundInfo.roundData.finalCards,
          winCombination: roundInfo.roundData.winCombination,
          status: currentBet.status,
        });
      }
    }
    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();
  }

  sendBet() {
    if (!gameStore.isBetsClosed && gameStore.isAllowSendToInvoke) {
      if (
        !freeBetStore.activeFreeBet.id &&
        (doBetStore.selectedAmount > userInfoStore.balance || !userInfoStore.balance)
      ) {
        notificationStore.setNotificationType(betResponseCodeByName.NotEnoughBalance);
        setTimeout(() => notificationStore.setNotificationType(null), 1000);
        gameStore.setIsBetsClosed(false);
      } else {
        gameStore.setIsBetsClosed(true);
        doBetStore.setIsShowGamble(false);
        doBetStore.setGambleType(null);
        gameStore.setIsAllowSendToInvoke(false);
        const { [DefaultQueryParams.GameTypeId]: gameTypeId } = parseQuery(window.location.search);
        cardsStore.removeWinInCards(true);
        if (applicationStore.isDemo) {
          gameLiveConnection.doDemoBet(doBetStore.selectedAmount);
        } else {
          gameLiveConnection.doBet(
            freeBetStore.activeFreeBet.id
              ? {
                  amount: doBetStore.selectedAmount,
                  gameTypeId,
                  freeBetId: freeBetStore.activeFreeBet.id,
                }
              : {
                  amount: doBetStore.selectedAmount,
                  gameTypeId,
                }
          );
        }
      }
    }
  }

  handleDoBetResult({
    finishedRound,
    betResult: { id, status, balance, roundId, winAmount, freeBetId },
  }) {
    gameStore.setBetId(id);
    this.handleRoundDoBetResult({
      status,
      balance,
      roundId,
      winAmount,
      freeBetId,
      cards: finishedRound.roundData.initialCards,
      winCombination: finishedRound.roundData.winCombination,
    });
  }

  handleDoBetResultValidation({ betResult: { responseCode } }) {
    notificationStore.setNotificationType(responseCode);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
    gameStore.setIsBetsClosed(false);
    gameStore.setIsAllowSendToInvoke(true);
  }

  sendHold() {
    if (gameStore.isAllowSendToInvoke) {
      const { heldCards } = cardsStore;
      const { betId } = gameStore;
      gameStore.setIsAllowSendToInvoke(false);
      if (applicationStore.isDemo) {
        gameLiveConnection.doDemoHold(heldCards);
      } else {
        gameLiveConnection.sendHold({
          HoldedCards: heldCards,
          BetId: betId,
        });
      }
    }
  }

  handleHoldResult({
    roundResult: { finalCards, initialCards, winCombination },
    status,
    winAmount,
  }) {
    this.handleRoundHoldResult({
      status,
      cards: finalCards.length ? finalCards : initialCards,
      winAmount,
      winCombination,
    });
  }

  handleCashOutResult({
    roundResult: { initialCards, finalCards, gambleResults, winCombination },
    status,
    winAmount,
    freeBetId,
  }) {
    this.handleRoundCashOutResult({
      status,
      // eslint-disable-next-line no-nested-ternary
      cards: gambleResults
        ? gambleResults[gambleResults.length - 1].gambleCards
        : finalCards.length
        ? finalCards
        : initialCards,
      winAmount,
      winCombination,
      freeBetId,
    });
  }

  handleDoDemoBetResult({ amount, winAmount, status, result: { initialCards, winCombination } }) {
    const { balance } = userInfoStore;
    this.handleRoundDoBetResult({
      status,
      balance,
      amount,
      cards: initialCards,
      winAmount,
      winCombination,
    });
  }

  handleDemoHoldResult({ status, responseCode, winAmount, result }) {
    if (responseCode) {
      this.handleResetDataInitial();
    } else {
      const { finalCards, initialCards, winCombination } = result;
      this.handleRoundHoldResult({
        status,
        cards: finalCards?.length ? finalCards : initialCards,
        winAmount,
        winCombination,
      });
    }
  }

  handleDemoCashoutResult({
    status,
    winAmount,
    result: { initialCards, finalCards, gambleResults, winCombination },
  }) {
    this.handleRoundCashOutResult({
      status,
      // eslint-disable-next-line no-nested-ternary
      cards: gambleResults
        ? gambleResults[gambleResults.length - 1].gambleCards
        : finalCards?.length
        ? finalCards
        : initialCards,
      winAmount,
      winCombination,
    });
  }

  handleGambleCardsResult(cards) {
    this.handleRoundGambleCardsResult(cards);
  }

  handleDemoGambleCardsResult(cards) {
    this.handleRoundGambleCardsResult(cards);
  }

  handleGambleResult(payload) {
    this.handleRoundGambleResult(payload);
  }

  handleDemoGambleResult(payload) {
    this.handleRoundGambleResult(payload);
  }

  handleLiveBetsResult(liveBet) {
    setTimeout(() => {
      liveBetsDataStore.updateLiveBets(liveBet);
    }, 600);
  }

  handleRoundDoBetResult({
    status,
    balance,
    roundId,
    amount,
    cards,
    winAmount,
    winCombination,
    freeBetId,
  }) {
    gameStore.setRoundStatus(status);
    if (!freeBetId) {
      if (applicationStore.isDemo) {
        userInfoStore.setDemoBalance(balance - amount);
      } else {
        userInfoStore.setBalance(balance);
        roundInfoStore.setRoundId(roundId);
      }
    }
    cardsStore.closeCards();
    winningStore.setWinCombination(null);
    setTimeout(() => {
      cardsStore.updateCards(cards, true);
      setTimeout(() => {
        cardsStore.setTableCards(cards, true);
        gameStore.setIsAllowSendToInvoke(true);
      }, 700);
    }, 300);
    this.handleRoundWinning({ winAmount, cards, winCombination });
  }

  handleRoundHoldResult({ status, cards, winAmount, winCombination }) {
    gameStore.setRoundStatus(status);
    cardsStore.closeCards();
    winningStore.setWinCombination(null);
    setTimeout(() => {
      cardsStore.updateCards(cards, false, true);
    }, 300);
    setTimeout(() => {
      gameStore.setIsAllowSendToInvoke(true);
    }, 400);
    this.handleRoundWinning({ winAmount, cards, winCombination, status });
  }

  handleRoundCashOutResult({ status, cards, winAmount, winCombination, freeBetId }) {
    gameStore.setRoundStatus(status);
    winningStore.setWinCombination(null);
    setTimeout(() => {
      cardsStore.setWithOutWinCards(cards, true);
    }, 300);
    setTimeout(() => {
      gameStore.setIsAllowSendToInvoke(true);
    }, 400);
    this.handleRoundWinning({ winAmount, cards, winCombination, freeBetId });
  }

  handleRoundGambleCardsResult(cards) {
    cardsStore.removeWinInCards(true);
    setTimeout(() => {
      cardsStore.closeCards();
      setTimeout(() => {
        cardsStore.setWithOutWinCards(cards);
        cardsStore.setIsPickHoldActive(true);
        gameStore.setIsAllowSendToInvoke(true);
        cardsStore.setIsShowGambleWin(true);
      }, 300);
    }, 300);
  }

  handleRoundGambleResult({ winAmount, cards, status }) {
    gameStore.setRoundStatus(status);
    cardsStore.setIsPickHoldActive(false);
    setTimeout(() => {
      soundService.play(soundTypes.cardHold);
      cardsStore.setWinInCards(cards, true);
      winningStore.setGambleWin(winAmount);
      gameStore.setIsAllowSendToInvoke(true);
    }, 300);

    if (status === BetStatus.Lose) {
      setTimeout(() => {
        this.handleResetEndedRoundData();
      }, 500);
    }
  }

  handleRoundWinning({ winAmount, cards, winCombination, status, freeBetId }) {
    setTimeout(() => {
      if (applicationStore.isDemo) {
        if (status !== BetStatus.Pending) {
          userInfoStore.setDemoBalance(userInfoStore.balance + winAmount);
        }
      } else {
        gameLiveConnection.getBalance();
      }
      freeBetStore.handleFreeBetEndedData();
    }, 500);
    if (winAmount) {
      if (status === BetStatus.Pending) {
        doBetStore.setIsShowGamble(true);
        setTimeout(() => {
          setTimeout(() => {
            winningStore.setGambleWin(winAmount);
            cardsStore.setWinInCards(cards);
            winningStore.setWinCombination(winCombination);
          }, 300);
        }, 500);
      } else {
        setTimeout(() => {
          if (freeBetId) {
            freeBetStore.freeBetTotalWinCalculator({ freeBetId, winAmount });
          }
          setTimeout(() => {
            cardsStore.setWinInCards(cards, Number.isInteger(doBetStore.gambleType));
            winningStore.setWinCombination(winCombination);
            winningStore.setWinningAmount(winAmount);
            soundService.playWin();
          }, 300);
          setTimeout(() => {
            this.handleResetEndedRoundData();
            winningStore.setLastWin(winAmount);
          }, 1500);
        }, 500);
      }
    } else {
      setTimeout(() => {
        winningStore.setWinCombination(winCombination);
        this.handleResetEndedRoundData();
      }, 500);
    }
  }

  handleResetEndedRoundData() {
    if (gameStore.roundStatus !== BetStatus.Pending) {
      if (!applicationStore.isDemo) {
        tableBetHistoryStore.handleBetHistoryUpdate();
      }
      gameStore.handleClearRoundData();
      roundInfoStore.setRoundId(null);
      winningStore.clearWinInfo();
    }
    cardsStore.setPickCardIndex(null);
    cardsStore.setIsShowGambleWin(false);
  }

  handleResetDataInitial() {
    cardsStore.setIsShowGambleWin(false);
    gameStore.setRoundStatus(0);
    gameStore.setIsAllowSendToInvoke(true);
    winningStore.setLastWin(0);
    winningStore.clearWinInfo();
    cardsStore.setTableCards([]);
    gameStore.setIsBetsClosed(false);
  }
}

export const turboGameService = new TurboGameService();
